import { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import { parseISO } from 'date-fns';

import { GET_NEXT_VIDEO_IN_VIDEO_ASL } from '~/graphql/videos';
import { Query, QueryGetVideoNextInVideoAslArgs } from '~/types';
import { sleep } from '~/utils/sleep';

const compareByDueDate = (a, b) =>
  parseISO(a!.due_date).getTime() - parseISO(b!.due_date).getTime();

// this will work only for video assignment
const useRelatedAssignment = (videoId: string, skip: boolean, videoFinished: boolean) => {
  const [startRefetching, setStartRefetching] = useState(false);
  const [retry, setRetry] = useState(5);
  const [fetching, setFetching] = useState(false);

  const { data, loading, refetch, called } = useQuery<
    Pick<Query, 'getVideoNextInVideoAsl'>,
    QueryGetVideoNextInVideoAslArgs
  >(GET_NEXT_VIDEO_IN_VIDEO_ASL, {
    variables: {
      video_id: +videoId,
    },
    skip: skip || !videoId,
  });

  const assignments = useMemo(
    () =>
      [...(data?.getVideoNextInVideoAsl?.video_assignments || [])]
        ?.sort(compareByDueDate)
        .filter((asl) => {
          const videosToWatch = Number(asl?.continue_watching_videos?.length);
          const hasVideosToWatch = videosToWatch > 0;
          const noVideosToWatch = !hasVideosToWatch;
          return hasVideosToWatch || (noVideosToWatch && asl?.has_test);
        }),
    [data],
  );

  const closestAssignment = assignments?.[0];
  const unwatchedVideos = closestAssignment?.continue_watching_videos || [];
  // after video completion on front-end we should refetch until backend mark that video as watched
  const onlyCurrentVideoLeft =
    unwatchedVideos?.length === 1 && unwatchedVideos?.[0]?.id === videoId;
  const watched = unwatchedVideos?.length === 0;
  const nextVideos = unwatchedVideos?.filter((video) => video?.id !== videoId);
  const nextVideo = nextVideos?.[0];

  useEffect(() => {
    if (loading) {
      return;
    }

    if (startRefetching && onlyCurrentVideoLeft && retry > 0) {
      (async () => {
        process.env.NODE_ENV !== 'test' && (await sleep(600));
        await refetch();
        setRetry((old) => --old);
      })();

      return;
    }

    if (startRefetching) {
      setStartRefetching(false);
      setFetching(false);
    }
  }, [startRefetching, retry, onlyCurrentVideoLeft, refetch, loading]);

  useEffect(() => {
    if (videoFinished) {
      setStartRefetching(true);
      setFetching(true);
    }
  }, [videoFinished]);

  //reset on next video
  useEffect(() => {
    return () => {
      setStartRefetching(false);
      setRetry(5);
      setFetching(false);
    };
  }, [videoId]);

  return {
    assignment: closestAssignment,
    watched,
    nextVideo,
    loading: fetching || loading,
    called,
  };
};

export default useRelatedAssignment;
